import { useState } from 'react';
import { Pencil, MoreHorizontal, Users, Tag, Edit } from 'lucide-react';
import { useSelector } from 'react-redux';
import { Button } from '@/components/ui/Button';
import {
  DetailRow,
  PropertyCard,
  CollapseSection,
  SectionHeader,
} from '@/components/ui/DetailSection';
import CombinedPatternPreview from './modals/patternGallery/CombinedPatternPreview';
import { useMaterialPattern } from '../hooks/api';
import { RadioGroup, RadioGroupItem } from '@/components/ui/RadioGroup';
import { cn } from '@/utils/cn';
import { SolutionDetailViewProps } from '../types';
import Bar from '@/components/elements/Bar';
import ApprovalItemActions from '@/features/approval/components/ApprovalItemActions';

const SolutionDetailView = ({ data, onEdit, editDisable }: SolutionDetailViewProps) => {
  const permission = editDisable ? 0 : useSelector((state: any) => state.auth.pagePermission);
  const { foregroundPattern, backgroundPattern, loadingPatterns } = useMaterialPattern(
    data?.material_pattern
  );

  const hasImage = data?.images && data?.images.length > 0;
  const hasPattern = data?.material_pattern;

  const getInitialViewMode = () => {
    if (hasImage) return 'image';
    if (hasPattern) return 'pattern';
    return 'image';
  };

  const [viewMode, setViewMode] = useState(getInitialViewMode());

  const properties = data?.property_definition_set || [];
  const professionBonds = data?.profession_bond_set || [];
  const technicalReports = data?.technical_report_item_set || [];
  const referenceProducts = data?.reference_product_set || [];
  const classifications = data?.classifications || [];

  return (
    <div className="flex-1 overflow-y-auto bg-neutral-25/30">
      <Bar
        text="Detail"
        content={
          permission === 2
            ? [
                <Button
                  key={1}
                  variant="ghost"
                  size="icon-sm"
                  onClick={onEdit}
                  data-testid="solution-detail-edit-btn"
                >
                  <Pencil />
                </Button>,
              ]
            : undefined
        }
      />
      <ApprovalItemActions objectType="solution" objectId={data?.id} status={data?.status} />
      <div className="px-6">
        {/* Image/Pattern */}
        {(hasImage || hasPattern) && (
          <div className="py-4 border-b border-neutral-150">
            <RadioGroup
              value={viewMode}
              onValueChange={(val) => setViewMode(val)}
              className="flex gap-0 mb-3"
            >
              {hasImage && (
                <label
                  className={cn(
                    'inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium border rounded-l cursor-pointer transition-colors',
                    viewMode === 'image'
                      ? 'bg-primary-50 text-primary-700 border-primary-200'
                      : 'bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50'
                  )}
                  htmlFor="radio-image"
                >
                  <RadioGroupItem value="image" id="radio-image" className="sr-only" />
                  Obrázek
                </label>
              )}
              {hasPattern && (
                <label
                  className={cn(
                    'inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium border rounded-r cursor-pointer transition-colors',
                    viewMode === 'pattern'
                      ? 'bg-primary-50 text-primary-700 border-primary-200'
                      : 'bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50'
                  )}
                  htmlFor="radio-pattern"
                >
                  <RadioGroupItem value="pattern" id="radio-pattern" className="sr-only" />
                  Vzor
                </label>
              )}
            </RadioGroup>

            {viewMode === 'image' && hasImage && (
              <img
                src={data?.images[0].url}
                className="max-h-[280px] rounded border border-neutral-150"
              />
            )}

            {viewMode === 'pattern' && hasPattern && (
              <div className="w-[280px] h-[280px] border border-neutral-150 rounded overflow-hidden">
                {loadingPatterns ? (
                  <div className="flex items-center justify-center h-full">
                    <span className="text-sm text-neutral-500">Načítání vzoru...</span>
                  </div>
                ) : (
                  <CombinedPatternPreview
                    foreground={foregroundPattern}
                    foregroundColor={data.material_pattern.fg_color_hex || '#000000'}
                    foregroundScale={data.material_pattern.fg_scale}
                    background={backgroundPattern}
                    backgroundColor={data.material_pattern.bg_color_hex || '#FFFFFF'}
                    backgroundScale={data.material_pattern.bg_scale}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* Basic fields */}
        <div className="py-5 space-y-3 border-b border-neutral-150">
          <DetailRow label="Označení" value={data?.title || ''} />
          <DetailRow label="Funkce" value={data?.function || ''} />
          <DetailRow label="Forma" value={data?.form || ''} />
          <DetailRow label="Provedení" value={data?.execution || ''} />
        </div>

        {/* Vlastnosti */}
        <CollapseSection
          title="Vlastnosti"
          badge={properties.length}
          defaultOpen={properties.length > 0}
        >
          <div className="space-y-2 mt-2">
            {properties.map((property: any, i: number) => (
              <PropertyCard key={i} label={property.title} value={property.value || '—'} />
            ))}
          </div>
        </CollapseSection>

        {/* Profesní vazby */}
        <CollapseSection
          title="Profesní vazby"
          badge={professionBonds.length}
          defaultOpen={professionBonds.length > 0}
        >
          <div className="mt-2 grid grid-cols-2 gap-2">
            {professionBonds.map((bond: any, i: number) => (
              <div
                key={i}
                className="px-3 py-2 bg-white border border-neutral-150 rounded flex items-center gap-2"
              >
                <Users size={14} className="text-neutral-500" />
                <span className="text-sm text-neutral-800">{bond.title}</span>
              </div>
            ))}
          </div>
        </CollapseSection>

        {/* Technické zprávy */}
        <CollapseSection
          title="Technické zprávy"
          badge={technicalReports.length}
          defaultOpen={technicalReports.length > 0}
        >
          <div className="space-y-2 mt-2">
            {technicalReports.map((item: any, i: number) => (
              <PropertyCard key={i} label={item.title} value={item.description || '—'} />
            ))}
          </div>
        </CollapseSection>

        <CollapseSection
          title="Výrobky"
          badge={referenceProducts.length}
          defaultOpen={referenceProducts.length > 0}
        >
          <div className="space-y-2 mt-2">
            {referenceProducts.map((item: any, i: number) => (
              <PropertyCard key={i} label={item.title} value={item.status || '—'} />
            ))}
          </div>
        </CollapseSection>

        {/* Zatřídění */}
        <CollapseSection
          title="Zatřídění"
          badge={classifications.length}
          defaultOpen={classifications.length > 0}
        >
          <div className="mt-2 space-y-1">
            {classifications.map((cls: any, i: number) => (
              <div
                key={i}
                className="px-3 py-1.5 bg-white border border-neutral-150 rounded flex items-center gap-2 hover:border-primary-300 transition-colors"
              >
                <Tag size={12} className="text-neutral-400" />
                <span className="text-xs font-mono text-neutral-700">{cls.title}</span>
              </div>
            ))}
          </div>
        </CollapseSection>
      </div>
    </div>
  );
};

export default SolutionDetailView;
